"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { generateSlug } from "@/lib/seoUtils";
import { Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";

interface ArticleFormProps {
  mode: 'create' | 'edit';
  initialData?: {
    id: string;
    title: string;
    slug: string;
    intro: string | null;
    content: string;
    image_prompt_description: string | null;
    tags: string[]; // Array of tag names
  };
}

export function ArticleForm({ mode, initialData }: ArticleFormProps) {
  const router = useRouter();
  const { toast } = useToast();

  const [title, setTitle] = useState(initialData?.title || '');
  const [intro, setIntro] = useState(initialData?.intro || '');
  const [content, setContent] = useState(initialData?.content || '');
  const [imageDescription, setImageDescription] = useState(initialData?.image_prompt_description || '');
  const [tags, setTags] = useState(initialData?.tags?.join(', ') || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim() || !content.trim()) {
      toast({
        title: "שגיאה",
        description: "יש למלא כותרת ותוכן",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "שגיאה",
          description: "נדרשת התחברות",
          variant: "destructive"
        });
        return;
      }

      const slug = mode === 'create' ? generateSlug(title) : initialData!.slug;
      const tagNames = tags.split(',').map(t => t.trim()).filter(Boolean);

      let articleId: string;

      if (mode === 'create') {
        // Create new article
        const { data: newArticle, error: createError } = await supabase
          .from('articles')
          .insert({
            author_id: user.id,
            title: title.trim(),
            slug,
            intro: intro.trim() || null,
            content: content.trim(),
            image_prompt_description: imageDescription.trim() || null,
            is_public: false // Always start as draft
          })
          .select('id')
          .single();

        if (createError) throw createError;
        articleId = newArticle.id;

        toast({
          title: "הצלחה!",
          description: "המאמר נוצר כטיוטה"
        });
      } else {
        // Update existing article
        const { error: updateError } = await supabase
          .from('articles')
          .update({
            title: title.trim(),
            intro: intro.trim() || null,
            content: content.trim(),
            image_prompt_description: imageDescription.trim() || null,
          })
          .eq('id', initialData!.id);

        if (updateError) throw updateError;
        articleId = initialData!.id;

        toast({
          title: "הצלחה!",
          description: "המאמר עודכן בהצלחה"
        });
      }

      // Update tags using RPC function
      if (tagNames.length > 0) {
        const { error: tagsError } = await supabase.rpc('upsert_article_tags', {
          p_article_id: articleId,
          p_tag_names: tagNames
        });

        if (tagsError) {
          console.error('Error updating tags:', tagsError);
        }
      }

      // Generate image if in create mode and there's a description
      if (mode === 'create' && imageDescription.trim()) {
        setIsGeneratingImage(true);
        try {
          await supabase.functions.invoke('generate-article-image', {
            body: {
              article_id: articleId,
              title: title.trim(),
              image_description: imageDescription.trim()
            }
          });
        } catch (imageError) {
          console.error('Error generating image:', imageError);
          // Don't fail the whole operation if image generation fails
        }
        setIsGeneratingImage(false);
      }

      // Redirect to article page
      router.push(`/articles/${slug}`);
    } catch (error) {
      console.error('Error submitting article:', error);
      toast({
        title: "שגיאה",
        description: "נכשל בשמירת המאמר",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" dir="rtl">
      {/* Title */}
      <div>
        <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
          כותרת המאמר *
        </label>
        <Input
          id="title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="לדוגמה: 10 טיפים למתחילים במטבח"
          className="h-12 text-lg"
          required
        />
      </div>

      {/* Intro */}
      <div>
        <label htmlFor="intro" className="block text-sm font-medium text-gray-700 mb-2">
          פסקת פתיחה (אופציונלי)
        </label>
        <Textarea
          id="intro"
          value={intro}
          onChange={(e) => setIntro(e.target.value)}
          placeholder="פסקה קצרה שמסכמת את המאמר. תוצג בבולט מתחת לכותרת ומעל התמונה."
          rows={3}
          className="resize-none text-base"
        />
        <p className="text-xs text-gray-500 mt-1">
          פסקת פתיחה מקצועית כמו באתרי חדשות - מופיעה בגודל גדול מתחת לכותרת
        </p>
      </div>

      {/* Tags */}
      <div>
        <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-2">
          תגיות (מופרדות בפסיקים)
        </label>
        <Input
          id="tags"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="לדוגמה: בישול, טיפים, מתחילים"
          className="h-11"
        />
        <p className="text-xs text-gray-500 mt-1">
          הפרידו תגיות בפסיקים. לדוגמה: בישול, מתכונים, טיפים
        </p>
      </div>

      {/* Image Description */}
      <div>
        <label htmlFor="imageDescription" className="block text-sm font-medium text-gray-700 mb-2">
          תיאור לתמונת כותרת (אופציונלי)
        </label>
        <Textarea
          id="imageDescription"
          value={imageDescription}
          onChange={(e) => setImageDescription(e.target.value)}
          placeholder="תארו את התמונה שתרצו, למשל: 'שולחן עץ כפרי עם פסטה טרייה ועגבניות שרי'"
          rows={3}
          className="resize-none"
        />
        <p className="text-xs text-gray-500 mt-1">
          {mode === 'create'
            ? 'אם תשאירו ריק, נייצר תמונה על בסיס הכותרת. התמונה תיווצר לאחר שמירת המאמר.'
            : 'לשינוי התמונה, השתמשו בכפתור "צור תמונה מחדש" בעמוד המאמר'
          }
        </p>
      </div>

      {/* Content */}
      <div>
        <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
          תוכן המאמר (Markdown) *
        </label>
        <Textarea
          id="content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="כתבו את המאמר כאן. תוכלו להשתמש ב-Markdown לעיצוב:
# כותרת ראשית
## כותרת משנית
**טקסט מודגש**
*טקסט נטוי*
- פריט ברשימה"
          rows={20}
          className="font-mono text-sm resize-y"
          required
        />
        <p className="text-xs text-gray-500 mt-1">
          תומך ב-Markdown: כותרות (#), מודגש (**), נטוי (*), רשימות (-), ועוד
        </p>
      </div>

      {/* Markdown Preview */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-2">תצוגה מקדימה</h3>
        <div
          className="p-6 bg-gray-50 border border-gray-200 rounded-lg prose prose-sm max-w-none prose-headings:text-right prose-p:text-right prose-li:text-right prose-a:text-orange-600"
          dir="rtl"
          lang="he"
        >
          {content ? (
            <ReactMarkdown>{content}</ReactMarkdown>
          ) : (
            <p className="text-gray-400 text-center">התצוגה המקדימה תופיע כאן...</p>
          )}
        </div>
      </div>

      {/* Buttons */}
      <div className="flex gap-4 justify-end pt-6 border-t">
        <Button
          type="button"
          variant="outline"
          onClick={() => router.back()}
          disabled={isSubmitting || isGeneratingImage}
        >
          ביטול
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting || isGeneratingImage}
          className="gap-2"
        >
          {isSubmitting || isGeneratingImage ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {isGeneratingImage ? 'מייצר תמונה...' : 'שומר...'}
            </>
          ) : (
            mode === 'create' ? 'צור מאמר' : 'עדכן מאמר'
          )}
        </Button>
      </div>

      {mode === 'create' && (
        <p className="text-sm text-gray-500 text-center">
          המאמר ייווצר כטיוטה. תוכלו לפרסם אותו מעמוד המאמר.
        </p>
      )}
    </form>
  );
}
